'use client';

import React, { useEffect, useState } from 'react';
import Link from 'next/link';
import { systemAPI } from '@/services/api';
import ContentPagesLinks from './ContentPagesLinks/ContentPagesLinks';
import WhatsAppPopup from '../components/WhatsAppPopup';

const Footer = () => {
  const [systemSettings, setSystemSettings] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchSystemSettings = async () => {
      try {
        const settings = await systemAPI.getSystemSettings();
        setSystemSettings(settings);
      } catch (err) {
        setError(err.message);
        // Use default values if fetch fails
        setSystemSettings({
          siteName: 'Bus Ticket System',
          contactPhone: '01234567890 01234567890',
          address: 'Jashimuddin sharani road, office-902\nUttara sector-11, Dhaka\nHospital'
        });
      } finally {
        setLoading(false);
      }
    };

    fetchSystemSettings();
  }, []);

  // Use default values while loading or if there's an error
  const siteName = systemSettings?.siteName || 'Bus Ticket System';
  const contactPhone = systemSettings?.whatsappNumber || '01234567890 01234567890';
  const address = systemSettings?.address || 'Jashimuddin sharani road, office-902\nUttara sector-11, Dhaka\nHospital';
  const logoUrl = systemSettings?.logoUrl;

  // Social media links
  const facebookUrl = systemSettings?.facebookUrl;
  const twitterUrl = systemSettings?.twitterUrl;
  const instagramUrl = systemSettings?.instagramUrl;
  const youtubeUrl = systemSettings?.youtubeUrl;
  // Mobile app links
  const androidAppUrl = systemSettings?.androidAppUrl;
  const iosAppUrl = systemSettings?.iosAppUrl;
  // Construct full image URL using the image URL environment variable
  const fullLogoUrl = logoUrl ? `${process.env.NEXT_PUBLIC_IMAGE_URL || 'http://localhost:5000'}${logoUrl}` : null;

  return (
    <footer className="w-full">

      <div className="bg-green-800 text-white py-12">
        <div className="mx-auto px-4">

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-8">
            <div className="mb-6 md:mb-0">
              <div className="flex items-center space-x-4 mb-4">
                {fullLogoUrl ? (
                  <>
                    <img src={fullLogoUrl} alt={siteName} className="h-12 bg-white p-1 rounded-lg" />
                    <h3 className="text-white text-2xl font-bold">{siteName}</h3>
                  </>
                ) : (
                  <h3 className="text-white text-2xl font-bold">{siteName}</h3>
                )}
              </div>
              <p className="text-white">WhatsApp Number: {contactPhone}</p>
              <p className="text-white">24/7 Services</p>
              <p className="text-white whitespace-pre-line">{address}</p>
            </div>

            <div>
              <h4 className="text-white text-xl font-semibold mb-4">About</h4>
              <ul>
                <li className="mb-2"><a href="https://oursherpur.com/category/bus/" className="text-white hover:text-gray-200">Blog</a></li>
                <li className="mb-2"><a href="/faq" className="text-white hover:text-gray-200">FAQ</a></li>
              </ul>
            </div>

            <div>
              <h4 className="text-white text-xl font-semibold mb-4">Services</h4>
              <ul>
                <li className="mb-2"><Link href="/bus-reserve" className="text-white hover:text-gray-200">Bus Reservation</Link></li>
                <li className="mb-2"><Link href="/blood-donor" className="text-white hover:text-gray-200">Blood Donor</Link></li>
                <li className="mb-2"><Link href="/car-rent" className="text-white hover:text-gray-200">Car Rent</Link></li>
              </ul>
            </div>

            <ContentPagesLinks />

            <div>
              <h4 className="text-white text-xl font-semibold mb-4">Download the app</h4>
              <div className="space-y-4">
                {iosAppUrl && (
                  <a href={iosAppUrl} target="_blank" rel="noopener noreferrer" className="flex items-center bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors">
                    <img src="/images/app-store.png" alt="App Store" className="h-12 mr-2" />
                  </a>
                )}
                {androidAppUrl && (
                  <a href={androidAppUrl} target="_blank" rel="noopener noreferrer" className="flex items-center bg-black text-white px-4 py-2 rounded-lg hover:bg-gray-800 transition-colors">
                    <img src="/images/google-play.png" alt="Google Play" className="h-12 mr-2" />
                  </a>
                )}
              </div>
            </div>
          </div>
          {/* Full-width payment image at the top */}
          <div className="border-t border-white mt-6 pt-6 flex flex-col md:flex-col justify-center items-center">
            {/* Full-width payment image at the top */}
            <div className="text-center w-full px-4">
              <img src="/images/footer-payment.jpg" alt="Payment Methods" className="h-auto max-w-full md:max-w-screen-lg mx-auto inline-block rounded-md shadow-sm" />
            </div>
            <div className='mt-8 flex flex-col md:flex-row w-full justify-between items-center gap-4'>
              <p className="text-white text-center md:text-left">&copy; 2025 {siteName} - All Rights Reserved</p>
              <div className="flex items-center space-x-4">
                <span className="text-white">Follow Us On :</span>
                {instagramUrl && (
                  <a href={instagramUrl} target="_blank" rel="noopener noreferrer" className="text-white hover:text-gray-200">
                    <svg
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      width="24"
                      height="24"
                      className="bg-white"
                    >
                      <defs>
                        <linearGradient id="igGradient" x1="0%" y1="0%" x2="100%" y2="100%">
                          <stop offset="0%" stop-color="#F58529" />
                          <stop offset="30%" stop-color="#DD2A7B" />
                          <stop offset="60%" stop-color="#8134AF" />
                          <stop offset="100%" stop-color="#515BD4" />
                        </linearGradient>
                      </defs>

                      <rect x="2" y="2" width="20" height="20" rx="5" fill="url(#igGradient)" />
                      <circle cx="12" cy="12" r="4.5" fill="white" />
                      <circle cx="17.5" cy="6.5" r="1.2" fill="white" />
                    </svg>

                  </a>
                )}
                {twitterUrl && (
                  <a href={twitterUrl} target="_blank" rel="noopener noreferrer" className="text-white hover:text-gray-200">
                    <svg
                      className="w-6 h-6 text-sky-500"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M23 4.6c-.8.4-1.6.6-2.5.8.9-.6 1.6-1.4 1.9-2.4-.8.5-1.8.9-2.7 1.1A4.27 4.27 0 0 0 16.6 3c-2.4 0-4.3 1.9-4.3 4.3 0 .3 0 .7.1 1C8.7 8.1 5.4 6.4 3.2 3.9c-.4.6-.6 1.4-.6 2.2 0 1.5.8 2.9 2 3.7-.7 0-1.4-.2-2-.5v.1c0 2.1 1.5 3.9 3.5 4.3-.4.1-.8.2-1.3.2-.3 0-.6 0-.8-.1.6 1.8 2.3 3.1 4.4 3.1A8.6 8.6 0 0 1 2 18.6 12.1 12.1 0 0 0 8.6 20.5c7.9 0 12.2-6.6 12.2-12.2v-.6c.8-.6 1.5-1.3 2.2-2.1z" />
                    </svg>

                  </a>
                )}
                {systemSettings?.facebookUrl && (
                  <a href={facebookUrl} target="_blank" rel="noopener noreferrer" className="text-white hover:text-gray-200">
                    <svg
                      className="w-6 h-6 text-[#1877F2] bg-white"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M22 12.07C22 6.48 17.52 2 11.93 2S1.86 6.48 1.86 12.07c0 4.96 3.66 9.06 8.44 9.93v-7.02H7.9v-2.91h2.4V9.85c0-2.37 1.41-3.68 3.57-3.68 1.03 0 2.11.18 2.11.18v2.33h-1.19c-1.17 0-1.54.73-1.54 1.48v1.78h2.63l-.42 2.91h-2.21V22c4.78-.87 8.44-4.97 8.44-9.93z" />
                    </svg>

                  </a>
                )}
                {youtubeUrl && (
                  <a href={youtubeUrl} target="_blank" rel="noopener noreferrer" className="text-white hover:text-gray-200">
                    <svg
                      className="w-6 h-6 text-[#FF0000] bg-white"
                      xmlns="http://www.w3.org/2000/svg"
                      viewBox="0 0 24 24"
                      fill="currentColor"
                    >
                      <path d="M23.5 6.2s-.23-1.64-.94-2.36c-.9-.94-1.9-.94-2.36-1C16.9 2.5 12 2.5 12 2.5h-.01s-4.9 0-8.2.34c-.46.06-1.46.06-2.36 1-.71.72-.94 2.36-.94 2.36S.5 8.05.5 9.9v1.78c0 1.85.23 3.7.23 3.7s.23 1.64.94 2.36c.9.94 2.08.91 2.61 1 1.9.18 8.22.24 8.22.24s4.9-.01 8.2-.35c.46-.05 1.46-.05 2.36-1 .71-.72.94-2.36.94-2.36s.23-1.85.23-3.7V9.9c0-1.85-.23-3.7-.23-3.7zM9.75 14.65V8.98l5.5 2.83-5.5 2.84z" />
                    </svg>


                  </a>
                )}
              </div>

            </div>
          </div>
        </div>
      </div>
      <WhatsAppPopup />
    </footer>
  );
};

export default Footer;